// src/pages/FollowingFeed.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../components/AuthGate";
import {
  fetchFollowingFeed,
  fetchFollowingUsers,
  fetchSocialPostsFeed,
  fetchSocialReactionsSummary,
  fetchUserBets,
  deleteSocialPost,
  toggleSocialReaction,
} from "../api";
import SocialPickCard from "../components/SocialPickCard";
import SocialPostCard, { postCreatedAt, postIdOf } from "../components/SocialPostCard";
import SocialPostComposer from "../components/SocialPostComposer";
const DEBUG_REACTIONS = false;

const toTs = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const normalizeTipsterFeed = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.picks || [];
  return rows
    .filter(Boolean)
    .map((row, idx) => ({
      ...row,
      id: row.id ?? `tipster-${idx}-${row.created_at || "x"}`,
      comment_target_type: "tipster_pick",
      comment_target_id: row.id ?? null,
      reaction_target_type: "tipster_pick",
      reaction_target_id: row.id ?? null,
      _source: "tipster",
    }));
};

const normalizeUserBetToFeedPick = (bet, owner, idx) => {
  const ownerName = owner?.display_name || owner?.username || "User";
  const ownerIdentifier = owner?.username || owner?.firebase_uid || owner?.id || `user-${idx}`;
  const handle = owner?.username ? `@${owner.username}` : "";
  return {
    id: `user-${ownerIdentifier}-${bet?.id ?? idx}`,
    tipster_username: owner?.username || null,
    tipster_handle: handle,
    tipster_name: ownerName,
    profile_identifier: String(ownerIdentifier),
    tipster_avatar: owner?.avatar_url || null,
    comment_target_type: "user_bet",
    comment_target_id: bet?.id ?? null,
    reaction_target_type: "user_bet",
    reaction_target_id: bet?.id ?? null,
    bet_type: bet?.bet_type || (bet?.is_acca ? "acca" : "single"),
    is_acca: !!bet?.is_acca,
    acca_legs: Array.isArray(bet?.acca_legs) ? bet.acca_legs : [],
    teams: bet?.teams || null,
    fixture_id: bet?.fixture_id ?? null,
    fixture_label: bet?.fixture_label || bet?.teams || bet?.matchup || "Bet",
    market: bet?.market || "Pick",
    bookmaker: bet?.bookmaker || "",
    price: bet?.price,
    stake: bet?.stake,
    result: bet?.result || null,
    created_at: bet?.created_at || null,
    profit: bet?.profit,
    _source: "user",
  };
};

const normalizeUserFeed = (usersWithBets = []) => {
  const merged = [];
  usersWithBets.forEach(({ owner, bets }) => {
    if (!Array.isArray(bets)) return;
    bets.forEach((bet, idx) => {
      merged.push(normalizeUserBetToFeedPick(bet, owner, idx));
    });
  });
  return merged;
};

const normalizeSocialPostFeed = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.posts || payload?.items || [];
  return rows.filter(Boolean).map((post, idx) => ({
    ...post,
    id: postIdOf(post) ?? `post-${idx}`,
    created_at: postCreatedAt(post) || post.created_at || null,
    _source: "social_post",
  }));
};

export default function FollowingFeed() {
  const { user, initializing } = useAuth();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");                       // 👈
  const [reactionsByKey, setReactionsByKey] = useState({});
  const [reactionLoadingByKey, setReactionLoadingByKey] = useState({});
  const [reactionErrByKey, setReactionErrByKey] = useState({});
  const [followingOnly, setFollowingOnly] = useState(false);
  const hydrateSeqRef = useRef(0);

  const getReactionTarget = (item) => {
    const target_type =
      item?.target_type ||
      item?.reaction_target_type ||
      item?.comment_target_type ||
      item?.comment_target ||
      (item?._source === "social_post" ? "user_post" : null) ||
      (item?._source === "user" ? "user_bet" : "tipster_pick");
    const target_id = item?.reaction_target_id ?? item?.comment_target_id ?? item?.id ?? null;
    if (!target_type || target_id === null || target_id === undefined || target_id === "") return null;
    return { target_type, target_id };
  };

  const reactionKeyOf = ({ target_type, target_id }) => `${target_type}:${target_id}`;

  const parseSummary = (summary) => {
    const like = summary?.reactions?.like || summary?.like || summary || {};
    return {
      count: Number(like?.count ?? summary?.count ?? 0) || 0,
      reacted_by_me: Boolean(like?.reacted_by_me ?? summary?.reacted_by_me ?? summary?.reacted ?? false),
    };
  };

  useEffect(() => {
    if (!user) {
      setFeed([]);
      setErr("");
      setReactionsByKey({});
      setReactionLoadingByKey({});
      setReactionErrByKey({});
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [tipsterFeedRaw, followingUsersRaw, socialPostsRaw] = await Promise.all([
          fetchFollowingFeed().catch(() => []),
          fetchFollowingUsers().catch(() => []),
          fetchSocialPostsFeed({ following_only: followingOnly, limit: 30 }).catch(() => []),
        ]);

        const tipsterFeed = normalizeTipsterFeed(tipsterFeedRaw);
        const followingUsers = Array.isArray(followingUsersRaw) ? followingUsersRaw : [];
        const socialPosts = normalizeSocialPostFeed(socialPostsRaw);

        const usersToLoad = followingUsers.slice(0, 20);
        const userBetsResults = await Promise.allSettled(
          usersToLoad.map(async (owner) => {
            const identifier = owner?.username || owner?.firebase_uid || owner?.id;
            if (!identifier) return { owner, bets: [] };
            try {
              const bets = await fetchUserBets(identifier);
              const rows = Array.isArray(bets) ? bets : [];
              const sorted = rows
                .filter(Boolean)
                .sort((a, b) => toTs(b?.created_at) - toTs(a?.created_at))
                .slice(0, 6);
              return { owner, bets: sorted };
            } catch {
              return { owner, bets: [] };
            }
          })
        );

        const userFeed = normalizeUserFeed(
          userBetsResults
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value)
        );

        const merged = [...socialPosts, ...tipsterFeed, ...userFeed].sort(
          (a, b) => toTs(postCreatedAt(b) || b?.created_at) - toTs(postCreatedAt(a) || a?.created_at)
        );

        if (!cancelled) {
          setFeed(merged);
        }
      } catch (e) {
        console.error("Failed to load following feed", e);
        const status = e?.response?.status;
        if (!cancelled) {
          if (status === 404) {
            setFeed([]);
            setErr("");
          } else {
            setErr("Could not load feed.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [followingOnly, user]);

  useEffect(() => {
    if (feed.length) return;
    setReactionsByKey({});
    setReactionLoadingByKey({});
    setReactionErrByKey({});
  }, [feed]);

  useEffect(() => {
    if (!user || !feed.length) return undefined;
    let cancelled = false;
    const requestSeq = ++hydrateSeqRef.current;
    const hydrate = async () => {
      const targets = feed
        .map(getReactionTarget)
        .filter(Boolean)
        .reduce((acc, target) => {
          const key = reactionKeyOf(target);
          if (!acc.some((x) => reactionKeyOf(x) === key)) acc.push(target);
          return acc;
        }, []);

      await Promise.allSettled(
        targets.map(async (target) => {
          const key = reactionKeyOf(target);
          setReactionLoadingByKey((prev) => ({ ...prev, [key]: true }));
          try {
            const summary = await fetchSocialReactionsSummary(target);
            const parsed = parseSummary(summary);
            if (DEBUG_REACTIONS) {
              console.log("[reactions:hydrate]", target.target_type, target.target_id, summary);
            }
            if (!cancelled && hydrateSeqRef.current === requestSeq) {
              setReactionsByKey((prev) => ({ ...prev, [key]: parsed }));
              setReactionErrByKey((prev) => ({ ...prev, [key]: "" }));
            }
          } catch (e) {
            if (!cancelled && hydrateSeqRef.current === requestSeq) {
              setReactionErrByKey((prev) => ({ ...prev, [key]: "Reaction unavailable" }));
            }
          } finally {
            if (!cancelled && hydrateSeqRef.current === requestSeq) {
              setReactionLoadingByKey((prev) => ({ ...prev, [key]: false }));
            }
          }
        })
      );
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [feed, user]);

  const handleToggleReaction = async (pick) => {
    const target = getReactionTarget(pick);
    if (!target) return;
    const key = reactionKeyOf(target);
    setReactionLoadingByKey((prev) => ({ ...prev, [key]: true }));
    try {
      const payload = { ...target, reaction_type: "like" };
      if (DEBUG_REACTIONS) {
        console.log("[reactions:toggle:payload]", payload);
      }
      const res = await toggleSocialReaction(payload);
      if (DEBUG_REACTIONS) {
        console.log("[reactions:toggle:response]", res);
      }
      const count = Number(res?.count ?? res?.reactions?.like?.count ?? 0) || 0;
      const reacted = Boolean(res?.reacted ?? res?.reacted_by_me ?? res?.reactions?.like?.reacted_by_me ?? false);
      setReactionsByKey((prev) => ({
        ...prev,
        [key]: { count, reacted_by_me: reacted },
      }));
      setReactionErrByKey((prev) => ({ ...prev, [key]: "" }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setErr("Please log in to react to picks.");
      }
      setReactionErrByKey((prev) => ({ ...prev, [key]: "Could not update reaction" }));
    } finally {
      setReactionLoadingByKey((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handlePostCreated = (post) => {
    const normalized = normalizeSocialPostFeed([post])[0];
    if (!normalized) return;
    setFeed((prev) =>
      [normalized, ...prev].sort(
        (a, b) => toTs(postCreatedAt(b) || b?.created_at) - toTs(postCreatedAt(a) || a?.created_at)
      )
    );
  };

  const handleDeletePost = async (postId) => {
    if (!postId) return;
    try {
      await deleteSocialPost(postId);
      setFeed((prev) => prev.filter((item) => item._source !== "social_post" || String(postIdOf(item)) !== String(postId)));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not delete post.");
    }
  };

  if (initializing) return null;

  // 1️⃣ Not logged in → prompt to log in
  if (!user) {
    return (
      <div className="page">
        <div className="card">
          <h2>Following Feed</h2>
          <p>You need to be logged in to see your following feed.</p>
          <Link to="/login" className="btnPrimary">
            Log in
          </Link>
          <p style={{ marginTop: 8, fontSize: "0.9rem", opacity: 0.8 }}>
            Once you’re logged in, follow tipsters and users from the{" "}
            <Link to="/tipsters">Tipsters</Link> page and their latest picks
            will appear here.
          </p>
        </div>
      </div>
    );
  }

  const isEmpty = !loading && !err && (!feed || feed.length === 0);

  return (
    <div className="page" style={{ maxWidth: 800, margin: "0 auto" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 24
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Your Feed</h2>
          <p style={{ margin: "8px 0 0", fontSize: 14, opacity: 0.7 }}>
            Latest posts and picks from the community
          </p>
        </div>
        <Link
          to="/tipsters"
          style={{
            padding: "10px 20px",
            borderRadius: 999,
            border: "1px solid #34d399",
            background: "rgba(52,211,153,0.1)",
            color: "#34d399",
            fontSize: 14,
            fontWeight: 600,
            textDecoration: "none",
            transition: "all 0.2s"
          }}
        >
          Find Users
        </Link>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {[
          [false, "All community"],
          [true, "Following only"],
        ].map(([value, label]) => {
          const active = followingOnly === value;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setFollowingOnly(value)}
              style={{
                border: active ? "1px solid #34d399" : "1px solid rgba(255,255,255,0.12)",
                background: active ? "rgba(52,211,153,0.14)" : "rgba(255,255,255,0.04)",
                color: active ? "#86efac" : "#e5f4eb",
                borderRadius: 8,
                padding: "7px 11px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 40, opacity: 0.7 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <p>Loading your feed…</p>
        </div>
      )}

      {err && (
        <div style={{
          background: "rgba(210,59,59,0.1)",
          border: "1px solid rgba(210,59,59,0.3)",
          borderRadius: 12,
          padding: 20,
          color: "#d23b3b"
        }}>
          {err}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <SocialPostComposer onCreated={handlePostCreated} dark />
      </div>

      {isEmpty && !loading && (
        <div style={{
          background: "#0a0f0c",
          borderRadius: 16,
          padding: 40,
          textAlign: "center",
          border: "1px solid rgba(255,255,255,0.08)"
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
          <h3 style={{ margin: "0 0 12px", fontSize: 20 }}>Your feed is empty</h3>
          <p style={{ margin: "0 0 24px", opacity: 0.7, fontSize: 14 }}>
            Follow tipsters and users to see their picks here
          </p>
          <Link
            to="/tipsters"
            style={{
              display: "inline-block",
              padding: "12px 24px",
              borderRadius: 999,
              background: "#34d399",
              color: "#000",
              fontSize: 14,
              fontWeight: 700,
              textDecoration: "none",
              transition: "transform 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
            onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
          >
            Discover Tipsters
          </Link>
        </div>
      )}

      {!isEmpty && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {feed.map((item) =>
            item._source === "social_post" ? (
              <SocialPostCard
                key={`social_post:${postIdOf(item)}`}
                post={item}
                onDelete={handleDeletePost}
                dark
              />
            ) : (
              <SocialPickCard
                key={`${item.comment_target_type || item._source}:${item.comment_target_id || item.id}`}
                pick={item}
                reactionState={(() => {
                  const target = getReactionTarget(item);
                  if (!target) return null;
                  return reactionsByKey[reactionKeyOf(target)] || { count: 0, reacted_by_me: false };
                })()}
                reactionLoading={(() => {
                  const target = getReactionTarget(item);
                  if (!target) return false;
                  return !!reactionLoadingByKey[reactionKeyOf(target)];
                })()}
                reactionError={(() => {
                  const target = getReactionTarget(item);
                  if (!target) return "";
                  return reactionErrByKey[reactionKeyOf(target)] || "";
                })()}
                onToggleReaction={() => handleToggleReaction(item)}
              />
            )
          )}
        </div>
      )}

    </div>
  );
}
